"use client";

import ChipSelection from "../chip-selection/chip-selection";
import { Separator } from "@repo/ui/components/ui/separator";
import { Input } from "@repo/ui/components/ui/input";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { FaqCategory, Portal, useFaqs } from "@repo/next-shared/data/faq";
import HelpItems from "@repo/next-shared/components/help-page/help-items";
import { usePathname, useSearchParams } from "next/navigation";
import { cn } from "@repo/ui/lib/utils";

type CategoryOption = {
  value: string;
  label: string;
};

const HelpPage = ({ portal }: { portal: Portal }) => {
  const t = useTranslations("SharedHelp");
  const params = useSearchParams();
  const path = usePathname();
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Array<string>>(
    [],
  );
  const { data: faqs } = useFaqs(portal, {
    query: search,
    categories: selectedCategories,
  });

  const isProfile = path.split("/").includes("profile");

  const [categories, setCategories] = useState<Array<CategoryOption>>([]);

  useEffect(() => {
    if (faqs) {
      const newCategories = [
        ...categories,
        ...faqs.map((category: FaqCategory) => ({
          value: category.id,
          label: category.title,
        })),
      ];
      const unique = [
        ...new Map(newCategories.map((obj) => [obj.value, obj])).values(),
      ] as Array<any>;
      setCategories(unique);
    }
  }, [faqs, search]);

  useEffect(() => setSearch(params?.get("search") || ""), [params]);

  function toggleCategory(selectedCategoriesIds: string[]) {
    setSelectedCategories(selectedCategoriesIds);
  }

  return (
    <div className="w-full">
      <div className="text-4xl">
        <h1 className={cn({ "text-[30px]": isProfile })}>{t("title")}</h1>
        <div className="w-full mt-10 max-md:mt-4" id="search-form">
          <Input
            rounded
            icon="Search"
            actionIcon
            tabIndex={2}
            id="help"
            name="help"
            label={t("input-label")}
            type="search"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </div>
      </div>

      {faqs?.length === 0 && (
        <div className="mt-[20px] mb-[80px]">{t("no-results")}</div>
      )}

      {faqs?.length > 0 && (
        <>
          <Separator className="my-10 max-md:my-4 dark: bg-secondary-gray3"></Separator>
          <div className="max-md:mt-4">
            <ChipSelection
              onSelectionChange={toggleCategory}
              title={t("categories-title")}
              items={categories}
              isProfile={isProfile}
            ></ChipSelection>
          </div>
          {faqs.map((f: FaqCategory) => {
            return (
              <div className="mt-5 max-md:mt-4" key={f.id}>
                <h4>{f.title}</h4>
                <HelpItems items={f.items} />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default HelpPage;
